/**
 * Site-wide table export: the `Export` button every data page puts in its
 * header, and the dialog it opens (Play Archive, Game Summary, Stat Tracker,
 * Playbook, Roster, Events, Team Users, Fanatics).
 *
 * The page hands over what it is SHOWING — its columns (visible ones ticked,
 * the rest available), and its rows in its current sort + filter order — so
 * the file matches the screen. The dialog offers:
 *
 *   - Format: Hudl compatible (Play Archive only), Custom, and every saved
 *     template of the team (templates are shared across pages and staff);
 *   - Custom: include / exclude any column, rename headings, reorder, pick
 *     Excel or CSV; optional row filters (Playbook units + categories);
 *   - Save as template… / Save changes / Delete for templates;
 *   - Download → `POST /exports/v1/table`, saved with the page's file name.
 *
 * The same Hudl-page building blocks (`hudl-*` classes, `export-format.ts`)
 * keep both exports looking and behaving alike.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { OverlayDialog } from '../../components/overlay-dialog';
import { ApiError } from '../../lib/api-error';
import type {
  ExportColumn,
  ExportFileType,
  ExportPage,
  ExportTemplate,
  TableExportRequest,
} from '../../lib/api-types';
import { EXPORT_COPY, EXPORT_MAX_CELLS, EXPORT_MAX_COLUMNS } from '../../lib/constants';

import { exportErrorMessage, saveBlob, toMessage } from './export-format';

export type ExportCell = string | number | boolean | null;

/** One column the page can export. */
export interface ExportColumnDef {
  /** Stable key (the page's column id, `custom:<stat>` for team stats). */
  field: string;
  /** What the column is, for the picker ("Offensive formation"). */
  label: string;
  /** Default heading in the file (defaults to `label`). */
  header?: string;
  /** Shown on the page right now → ticked by default. */
  isVisible: boolean;
}

/** A row filter the dialog offers (e.g. Playbook units, categories). */
export interface ExportRowFilter {
  id: string;
  label: string;
  options: readonly { value: string; label: string }[];
  /** The option value of row `index`. */
  valueOf: (index: number) => string;
  /** Pre-ticked options (default: all). */
  initial?: readonly string[];
}

export interface TableExportSource {
  page: ExportPage;
  /** "Play Archive" — dialog title, toasts. */
  pageLabel: string;
  columns: readonly ExportColumnDef[];
  /** Rows in the page's sort + filter order, cells in `columns` order. */
  rows: () => readonly (readonly ExportCell[])[];
  /** File name without extension (`PlayArchive_Varsity_vs_Wayne_2026-09-25`). */
  fileStem: string;
  sheetName?: string;
  /** Play Archive: offers "Hudl compatible" for this event + these plays. */
  hudl?: { eventId: string; playIds: readonly string[] } | null;
  rowFilters?: readonly ExportRowFilter[];
  /** "plays", "players", "events" … for the row count. */
  rowNoun?: { one: string; many: string };
}

interface DraftColumn extends ExportColumn {
  label: string;
  isIncluded: boolean;
}

type FormatChoice = 'hudl' | 'custom' | `template:${string}`;

function pageDraft(columns: readonly ExportColumnDef[]): DraftColumn[] {
  return columns.map((column) => ({
    field: column.field,
    header: column.header ?? column.label,
    label: column.label,
    isIncluded: column.isVisible,
  }));
}

/** A template's columns first (in its order), then the page's other columns, unticked. */
export function templateDraft(
  template: ExportTemplate,
  columns: readonly ExportColumnDef[]
): DraftColumn[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const inTemplate = template.columns
    .filter((column) => byField.has(column.field))
    .map((column) => ({
      field: column.field,
      header: column.header,
      label: byField.get(column.field)?.label ?? column.field,
      isIncluded: true,
    }));
  const used = new Set(inTemplate.map((column) => column.field));
  const rest = columns
    .filter((column) => !used.has(column.field))
    .map((column) => ({
      field: column.field,
      header: column.header ?? column.label,
      label: column.label,
      isIncluded: false,
    }));
  return [...inTemplate, ...rest];
}

function included(draft: readonly DraftColumn[]): ExportColumn[] {
  return draft
    .filter((column) => column.isIncluded)
    .map((column) => ({ field: column.field, header: column.header.trim() || column.label }));
}

function sameColumns(left: readonly ExportColumn[], right: readonly ExportColumn[]): boolean {
  return (
    left.length === right.length &&
    left.every(
      (column, index) =>
        column.field === right[index]?.field && column.header === right[index]?.header
    )
  );
}

export interface TableExportButtonProps {
  /**
   * Built lazily when the dialog opens, so the page pays nothing until then.
   * May be async (the Playbook gathers every unit's options first).
   */
  source: () => TableExportSource | Promise<TableExportSource>;
  isDisabled?: boolean;
  className?: string;
}

/** The page-header `Export` button; opens the shared dialog. */
export function TableExportButton({
  source,
  isDisabled = false,
  className,
}: TableExportButtonProps): JSX.Element {
  const { showToast } = useToast();
  const [snapshot, setSnapshot] = useState<TableExportSource | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);

  async function open(): Promise<void> {
    setIsPreparing(true);
    try {
      setSnapshot(await source());
    } catch (caught) {
      showToast(exportErrorMessage(caught), 'error');
    } finally {
      setIsPreparing(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`btn btn--secondary btn--sm export-button${className ? ` ${className}` : ''}`}
        title={EXPORT_COPY.buttonTitle}
        disabled={isDisabled || isPreparing}
        aria-busy={isPreparing || undefined}
        onClick={() => void open()}
        data-testid="table-export-button"
      >
        <svg
          className="export-button__icon"
          viewBox="0 0 24 24"
          width="1em"
          height="1em"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M12 3v11m0 0l-4.5-4.5M12 14l4.5-4.5M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {EXPORT_COPY.button}
      </button>
      {snapshot === null ? null : (
        <TableExportDialog source={snapshot} onClose={() => setSnapshot(null)} />
      )}
    </>
  );
}

export interface TableExportDialogProps {
  source: TableExportSource;
  onClose: () => void;
}

export function TableExportDialog({ source, onClose }: TableExportDialogProps): JSX.Element {
  const api = useApiClient();
  const { showToast } = useToast();
  const ids = useId();
  const allRows = useMemo(() => source.rows(), [source]);
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [choice, setChoice] = useState<FormatChoice>('custom');
  const [draft, setDraft] = useState<DraftColumn[]>(() => pageDraft(source.columns));
  const [fileType, setFileType] = useState<ExportFileType>('xlsx');
  const [filterState, setFilterState] = useState<Record<string, ReadonlySet<string>>>(() =>
    Object.fromEntries(
      (source.rowFilters ?? []).map((filter) => [
        filter.id,
        new Set(filter.initial ?? filter.options.map((option) => option.value)),
      ])
    )
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listExportTemplates(controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setTemplates(page.items);
      })
      .catch(() => {
        // Templates are a convenience: Custom still works without them.
      });
    return () => controller.abort();
  }, [api]);

  const fields = useMemo(() => new Set(source.columns.map((column) => column.field)), [source]);
  const activeTemplate = choice.startsWith('template:')
    ? (templates.find((template) => `template:${template.id}` === choice) ?? null)
    : null;
  const draftColumns = included(draft);
  const templateColumns =
    activeTemplate === null
      ? []
      : activeTemplate.columns.filter((column) => fields.has(column.field));
  const isTemplateChanged =
    activeTemplate !== null &&
    (!sameColumns(draftColumns, templateColumns) || fileType !== activeTemplate.file_type);

  const rowIndexes = useMemo(() => {
    const filters = source.rowFilters ?? [];
    return allRows
      .map((_, index) => index)
      .filter((index) =>
        filters.every((filter) => filterState[filter.id]?.has(filter.valueOf(index)) !== false)
      );
  }, [allRows, filterState, source.rowFilters]);
  const rowCount = choice === 'hudl' ? (source.hudl?.playIds.length ?? 0) : rowIndexes.length;
  const noun = source.rowNoun ?? { one: 'row', many: 'rows' };
  const isOverLimit =
    choice !== 'hudl' &&
    (rowCount * draftColumns.length > EXPORT_MAX_CELLS || draftColumns.length > EXPORT_MAX_COLUMNS);

  const choose = useCallback(
    (next: FormatChoice): void => {
      setChoice(next);
      setError(null);
      if (next === 'custom') {
        setDraft(pageDraft(source.columns));
        setFileType('xlsx');
      } else if (next.startsWith('template:')) {
        const template = templates.find((entry) => `template:${entry.id}` === next);
        if (template !== undefined) {
          setDraft(templateDraft(template, source.columns));
          setFileType(template.file_type);
        }
      }
    },
    [source.columns, templates]
  );

  async function download(): Promise<void> {
    setIsDownloading(true);
    setError(null);
    try {
      const isHudl = choice === 'hudl' && source.hudl !== undefined && source.hudl !== null;
      const base: TableExportRequest = {
        page_name: source.page,
        file_name: source.fileStem,
        sheet_name: source.sheetName ?? source.pageLabel,
      };
      const request: TableExportRequest = isHudl
        ? {
            ...base,
            format: 'hudl',
            file_type: 'xlsx',
            event_id: source.hudl?.eventId,
            play_ids: source.hudl?.playIds,
          }
        : {
            ...base,
            format: 'custom',
            file_type: fileType,
            fields: source.columns.map((column) => column.field),
            rows: rowIndexes.map((index) => allRows[index] ?? []),
            ...(activeTemplate !== null && !isTemplateChanged
              ? { template_id: activeTemplate.id }
              : { columns: draftColumns }),
          };
      const file = await api.downloadTableExport(request);
      const name = file.file_name ?? `${source.fileStem}.${isHudl ? 'xlsx' : fileType}`;
      saveBlob(file.blob, name);
      setLastFile(name);
    } catch (caught) {
      setError(exportErrorMessage(caught));
    } finally {
      setIsDownloading(false);
    }
  }

  const formatOptions: { value: FormatChoice; label: string; hint?: string }[] = [
    ...(source.hudl ? [{ value: 'hudl' as const, label: EXPORT_COPY.hudl }] : []),
    { value: 'custom', label: EXPORT_COPY.custom },
    ...templates.map((template) => ({
      value: `template:${template.id}` as FormatChoice,
      label: template.name,
      hint: template.columns.some((column) => fields.has(column.field))
        ? undefined
        : EXPORT_COPY.noMatchingColumns,
    })),
  ];
  const isBlocked = choice !== 'hudl' && (draftColumns.length === 0 || rowIndexes.length === 0);

  const footer = (
    <div className="export-dialog__footer">
      <div className="export-dialog__status">
        {choice !== 'hudl' && draftColumns.length === 0 ? (
          <span className="muted">{EXPORT_COPY.pickColumn}</span>
        ) : choice !== 'hudl' && rowIndexes.length === 0 ? (
          <span className="muted">{EXPORT_COPY.noRows}</span>
        ) : isOverLimit ? (
          <span className="export-dialog__warn">{EXPORT_COPY.tooLargeHint}</span>
        ) : lastFile !== null && !isDownloading && error === null ? (
          <span className="hudl-card__done" role="status" title={lastFile}>
            {EXPORT_COPY.downloaded(lastFile)}
          </span>
        ) : null}
      </div>
      <div className="btn-row">
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void download()}
          disabled={isDownloading || isBlocked}
          aria-busy={isDownloading}
          data-testid="table-export-download"
        >
          {isDownloading ? <span className="hudl-spinner" aria-hidden="true" /> : null}
          {isDownloading ? EXPORT_COPY.preparing : EXPORT_COPY.download}
        </button>
      </div>
    </div>
  );

  return (
    <OverlayDialog
      title={EXPORT_COPY.dialogTitle(source.pageLabel)}
      onClose={onClose}
      panelClassName="overlay__panel--export"
      footer={footer}
    >
      <div className="export-dialog stack" data-testid="table-export-dialog">
        <fieldset className="hudl-choice">
          <legend className="field__label">{EXPORT_COPY.format}</legend>
          <div className="hudl-choice__options export-dialog__formats">
            {formatOptions.map((option) => (
              <label
                key={option.value}
                className={`hudl-choice__option${choice === option.value ? ' is-active' : ''}`}
                title={option.hint}
              >
                <input
                  type="radio"
                  className="visually-hidden"
                  name={`${ids}-format`}
                  value={option.value}
                  checked={choice === option.value}
                  onChange={() => choose(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <p className="field__hint">{EXPORT_COPY.templateHelp}</p>
        </fieldset>

        {error === null ? null : (
          <div className="banner banner--error" role="alert" data-testid="table-export-error">
            <p>{error}</p>
          </div>
        )}

        {choice === 'hudl' ? (
          <div className="hudl-format" data-testid="table-export-hudl">
            <p className="hudl-format__summary">{EXPORT_COPY.hudlSummary}</p>
            <p className="muted">
              {rowCount.toLocaleString('en-US')} {rowCount === 1 ? noun.one : noun.many}
            </p>
          </div>
        ) : (
          <ColumnEditor
            page={source.page}
            draft={draft}
            setDraft={setDraft}
            fileType={fileType}
            setFileType={setFileType}
            rowSummary={`${rowIndexes.length.toLocaleString('en-US')} ${
              rowIndexes.length === 1 ? noun.one : noun.many
            }`}
            rowFilters={source.rowFilters ?? []}
            filterState={filterState}
            setFilterState={setFilterState}
            activeTemplate={activeTemplate}
            isTemplateChanged={isTemplateChanged}
            onSaved={(template, isNew) => {
              setTemplates((current) =>
                isNew
                  ? [...current, template].sort((a, b) => a.name.localeCompare(b.name))
                  : current.map((entry) => (entry.id === template.id ? template : entry))
              );
              setChoice(`template:${template.id}`);
              showToast(
                isNew ? EXPORT_COPY.saved(template.name) : EXPORT_COPY.updated(template.name),
                'success'
              );
            }}
            onDeleted={(template) => {
              setTemplates((current) => current.filter((entry) => entry.id !== template.id));
              setChoice('custom');
              setDraft(pageDraft(source.columns));
              showToast(EXPORT_COPY.deleted(template.name), 'success');
            }}
          />
        )}
      </div>
    </OverlayDialog>
  );
}

interface ColumnEditorProps {
  page: ExportPage;
  draft: DraftColumn[];
  setDraft(update: (current: DraftColumn[]) => DraftColumn[]): void;
  fileType: ExportFileType;
  setFileType(value: ExportFileType): void;
  rowSummary: string;
  rowFilters: readonly ExportRowFilter[];
  filterState: Record<string, ReadonlySet<string>>;
  setFilterState(
    update: (current: Record<string, ReadonlySet<string>>) => Record<string, ReadonlySet<string>>
  ): void;
  activeTemplate: ExportTemplate | null;
  isTemplateChanged: boolean;
  onSaved(template: ExportTemplate, isNew: boolean): void;
  onDeleted(template: ExportTemplate): void;
}

function ColumnEditor({
  page,
  draft,
  setDraft,
  fileType,
  setFileType,
  rowSummary,
  rowFilters,
  filterState,
  setFilterState,
  activeTemplate,
  isTemplateChanged,
  onSaved,
  onDeleted,
}: ColumnEditorProps): JSX.Element {
  const api = useApiClient();
  const [templateName, setTemplateName] = useState(activeTemplate?.name ?? '');
  const [isNaming, setIsNaming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setIsNaming(false);
    setSaveError(null);
    setTemplateName(activeTemplate?.name ?? '');
  }, [activeTemplate]);

  function update(field: string, patch: Partial<DraftColumn>): void {
    setDraft((current) =>
      current.map((column) => (column.field === field ? { ...column, ...patch } : column))
    );
  }

  function move(index: number, step: -1 | 1): void {
    setDraft((current) => {
      const target = index + step;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target] as DraftColumn, next[index] as DraftColumn];
      return next;
    });
  }

  function toggleFilter(filterId: string, value: string, isOn: boolean): void {
    setFilterState((current) => {
      const next = new Set(current[filterId] ?? []);
      if (isOn) next.add(value);
      else next.delete(value);
      return { ...current, [filterId]: next };
    });
  }

  async function save(asNew: boolean): Promise<void> {
    const name = templateName.trim();
    if (name === '') {
      setSaveError(EXPORT_COPY.nameRequired);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const columns = included(draft);
      if (asNew || activeTemplate === null) {
        onSaved(
          await api.createExportTemplate({ name, page_name: page, file_type: fileType, columns }),
          true
        );
      } else {
        onSaved(
          await api.updateExportTemplate(activeTemplate.id, { name, file_type: fileType, columns }),
          false
        );
      }
      setIsNaming(false);
    } catch (caught) {
      if (ApiError.isApiError(caught) && caught.code === 'DUPLICATE_EXPORT_TEMPLATE_NAME') {
        setSaveError(EXPORT_COPY.duplicateName(name));
      } else {
        setSaveError(toMessage(caught, 'The template could not be saved. Please try again.'));
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (activeTemplate === null) return;
    setDeleteError(null);
    try {
      await api.deleteExportTemplate(activeTemplate.id);
      setIsConfirmingDelete(false);
      onDeleted(activeTemplate);
    } catch (caught) {
      setDeleteError(toMessage(caught, 'The template could not be deleted.'));
    }
  }

  const includedCount = draft.filter((column) => column.isIncluded).length;
  const isRenamed = activeTemplate !== null && templateName.trim() !== activeTemplate.name;

  return (
    <div className="hudl-editor export-dialog__editor" data-testid="table-export-editor">
      <p className="field__hint">{EXPORT_COPY.customHelp}</p>

      {rowFilters.map((filter) => (
        <fieldset key={filter.id} className="export-dialog__filter">
          <legend className="field__label">{filter.label}</legend>
          <div className="export-dialog__filter-options">
            {filter.options.map((option) => (
              <label key={option.value} className="checkbox checkbox--compact">
                <input
                  type="checkbox"
                  checked={filterState[filter.id]?.has(option.value) ?? true}
                  onChange={(event) => toggleFilter(filter.id, option.value, event.target.checked)}
                />
                <span className="checkbox__text">{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <div className="hudl-editor__bar">
        <p className="hudl-format__summary" data-testid="table-export-summary">
          <strong>{EXPORT_COPY.columnsIncluded(includedCount, draft.length)}</strong> · {rowSummary}
        </p>
        <div className="hudl-filetype" role="group" aria-label={EXPORT_COPY.fileType}>
          {(['xlsx', 'csv'] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={`hudl-filetype__option${fileType === type ? ' is-active' : ''}`}
              aria-pressed={fileType === type}
              onClick={() => setFileType(type)}
            >
              {type === 'xlsx' ? EXPORT_COPY.excel : EXPORT_COPY.csv}
            </button>
          ))}
        </div>
      </div>

      <ol className="hudl-columns export-dialog__columns" aria-label="Columns in the file">
        {draft.map((column, index) => (
          <li
            key={column.field}
            className={`hudl-column${column.isIncluded ? '' : ' is-excluded'}`}
            data-field={column.field}
          >
            <input
              type="checkbox"
              className="hudl-column__check"
              checked={column.isIncluded}
              onChange={(event) => update(column.field, { isIncluded: event.target.checked })}
              aria-label={EXPORT_COPY.include(column.label)}
            />
            <div className="hudl-column__names">
              <input
                type="text"
                className="hudl-column__header"
                value={column.header}
                maxLength={120}
                onChange={(event) => update(column.field, { header: event.target.value })}
                aria-label={EXPORT_COPY.heading(column.label)}
              />
              <span className="hudl-column__field">{column.label}</span>
            </div>
            <div className="hudl-column__tools">
              <button
                type="button"
                className="hudl-icon-btn"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={EXPORT_COPY.moveUp(column.label)}
                title="Move up"
              >
                <span aria-hidden="true">↑</span>
              </button>
              <button
                type="button"
                className="hudl-icon-btn"
                onClick={() => move(index, 1)}
                disabled={index === draft.length - 1}
                aria-label={EXPORT_COPY.moveDown(column.label)}
                title="Move down"
              >
                <span aria-hidden="true">↓</span>
              </button>
            </div>
          </li>
        ))}
      </ol>

      <div className="hudl-template">
        {activeTemplate === null ? (
          isNaming ? (
            <div className="hudl-template__row">
              <label className="field hudl-template__name">
                <span className="field__label">{EXPORT_COPY.templateName}</span>
                <input
                  className="field__control"
                  value={templateName}
                  maxLength={80}
                  placeholder={EXPORT_COPY.templateNamePlaceholder}
                  onChange={(event) => setTemplateName(event.target.value)}
                  autoFocus
                />
              </label>
              <div className="hudl-template__buttons">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => void save(true)}
                  disabled={isSaving}
                  data-testid="table-export-template-save"
                >
                  {isSaving ? 'Saving…' : EXPORT_COPY.saveTemplate}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setIsNaming(false);
                    setSaveError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ghost hudl-template__start"
              onClick={() => setIsNaming(true)}
              data-testid="table-export-template-new"
            >
              {EXPORT_COPY.saveAsTemplate}
            </button>
          )
        ) : (
          <div className="hudl-template__row">
            <label className="field hudl-template__name">
              <span className="field__label">{EXPORT_COPY.templateName}</span>
              <input
                className="field__control"
                value={templateName}
                maxLength={80}
                onChange={(event) => setTemplateName(event.target.value)}
              />
            </label>
            <div className="hudl-template__buttons">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void save(false)}
                disabled={isSaving || (!isTemplateChanged && !isRenamed)}
                data-testid="table-export-template-update"
              >
                {isSaving ? 'Saving…' : EXPORT_COPY.saveChanges}
              </button>
              <button
                type="button"
                className="btn btn--ghost hudl-danger-text"
                onClick={() => setIsConfirmingDelete(true)}
                data-testid="table-export-template-delete"
              >
                {EXPORT_COPY.deleteTemplate}
              </button>
            </div>
          </div>
        )}
        {saveError === null ? null : (
          <p className="field__error" role="alert">
            {saveError}
          </p>
        )}
      </div>

      {isConfirmingDelete && activeTemplate !== null ? (
        <ConfirmDialog
          title={EXPORT_COPY.deleteTitle}
          consequence={EXPORT_COPY.deleteConsequence(activeTemplate.name)}
          confirmLabel={EXPORT_COPY.deleteConfirm}
          tone="danger"
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => {
            setIsConfirmingDelete(false);
            setDeleteError(null);
          }}
        />
      ) : null}
    </div>
  );
}
